<template>
  <div class="new-contact-form">
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>
    <f7-row>
      <f7-col>
        <div class="card-container">
          <form @submit.prevent="saveNewContact">
            <DxForm
              id="user-personal-info"
              class="ha-form"
              :col-count="3"
              :form-data="formData"
              label-location="top"
            >
              <DxItem
                :editor-options="{ disabled: false }"
                data-field="name"
              >
                <DxRequiredRule
                  :message="$t('newContactForm.requiredName')"
                />
                <DxLabel :text="$t('name')" />
              </DxItem>

              <DxItem
                data-field="surname"
                :editor-options="{ disabled: false }"
              >
                <DxRequiredRule
                  :message="$t('newContactForm.requiredSurname')"
                />
                <DxLabel :text="$t('surname')" />
              </DxItem>

              <DxItem
                class="ha-form__language"
                :editor-options="{
                  items: formData.langs,
                  displayExpr: translateLanguageOption,
                  placeholder: $t('select'),
                }"
                data-field="language"
                editor-type="dxSelectBox"
              >
                <DxRequiredRule
                  :message="$t('newContactForm.requiredLang')"
                />
                <DxLabel :text="$t('language')" />
              </DxItem>

              <DxItem
                :editor-options="{
                  disabled: false,
                }"
                data-field="phone"
              >
                <DxRequiredRule
                  :message="$t('newContactForm.requiredPhone')"
                />
                <DxLabel :text="$t('phone')" />
              </DxItem>

              <DxItem
                data-field="email"
                :editor-options="{
                  disabled: editData.isEditing,
                  elementAttr: { id: 'itemEmail' },
                }"
              >
                <DxRequiredRule
                  v-if="!editData.isEditing"
                  :message="$t('newContactForm.requiredEmail')"
                />
                <DxEmailRule
                  v-if="!editData.isEditing"
                  :message="$t('newContactForm.invalidMail')"
                />
                <DxLabel :text="$t('email')" />
              </DxItem>
            </DxForm>

            <!-- Árbol de direcciones -->
            <f7-row>
              <f7-col width="100" class="treeview-column">
                <div class="treeview-container">
                  <p class="addresses-title">
                    {{ $t('addresses') }}:
                    <i style="color: #a80d0b">*</i>
                  </p>
                  <DxTreeView
                    class="custom-treeview"
                    :width="170"
                    :height="120"
                    :items="addresses"
                    show-check-boxes-mode="normal"
                    selection-mode="multiple"
                    :select-nodes-recursive="true"
                    :select-by-click="false"
                    display-expr="name"
                    @selection-changed="treeViewSelectionChanged"
                    @content-ready="treeViewContentReady"
                  />
                </div>
              </f7-col>
              <span
                v-if="notEnoughAddressesSelected"
                style="color: #A80D0B"
              >
                {{ $t('newContactForm.addressesRequired') }}
              </span>
            </f7-row>

            <!-- Botón Guardar -->
            <f7-row class="justify-content-start">
              <f7-col
                class="marginTop"
                width="100"
                style="text-align: left"
              >
                <DxButton
                  id="botonGuardar"
                  class="custom-button"
                  :use-submit-behavior="true"
                  :text="$t('save')"
                  type="outlined"
                />
              </f7-col>
            </f7-row>
          </form>
        </div>
      </f7-col>
    </f7-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { DxForm, DxItem, DxLabel } from 'devextreme-vue/form';
import DxButton from 'devextreme-vue/button';
import notify from 'devextreme/ui/notify';
import { ALL_LANGUAGES, NOTIFY_TIME_LONG } from '@/js/constants';
import DxTreeView from 'devextreme-vue/tree-view';
import {
  DxRequiredRule,
  DxEmailRule,
} from 'devextreme-vue/validator';

export default {
  components: {
    DxForm,
    DxItem,
    DxRequiredRule,
    DxLabel,
    DxButton,
    DxEmailRule,
    DxTreeView,
  },
  props: {},
  data() {
    return {
      formData: {
        name: '',
        surname: '',
        langs: ALL_LANGUAGES,
        phone: '',
        email: '',
        language: '',
      },
      addresses: [],
      selectedAddresses: [],
      notEnoughAddressesSelected: false,
      loaded: false,
    };
  },
  computed: {
    ...mapState('contact', {
      availableAddresses: 'addresses',
    }),
    ...mapState('clientMenu', ['items']),
    ...mapState('contact', ['company', 'editData']),
  },
  beforeMount() {
    this.addresses = this.availableAddresses;

    if (this.editData.isEditing) {
      this.formData.name = this.editData.data.name;
      this.formData.surname = this.editData.data.surname;
      this.formData.language = this.getLanguageObj(
        this.editData.data.lang
      );
      this.formData.phone = this.editData.data.phone;
      this.formData.email = this.editData.data.email;
    }
    this.loaded = true;
  },
  methods: {
    async saveNewContact() {
      if (!this.validate()) {
        this.$notifyDX(
          {
            message: this.$t('samplesRegister.missingFieldsError'),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
        return;
      }
      this.loaded = false;
      try {
        const data = this.editData.isEditing
          ? {
              name: this.formData.name,
              surname: this.formData.surname,
              language: this.formData.language.key,
              companyId: this.editData.data.companyId,
              contactCode: this.editData.data.contactCode,
              addresses: this.selectedAddresses,
              phone: this.formData.phone,
            }
          : {
              name: this.formData.name,
              surname: this.formData.surname,
              email: this.formData.email,
              language: this.formData.language.key,
              companyId: this.company,
              addresses: this.selectedAddresses,
              phone: this.formData.phone,
            };

        notify(
          {
            message: this.editData.isEditing
              ? this.$t('editing')
              : this.$t('contactCreating'),
            width: 450,
          },
          'info',
          2000
        );

        if (this.editData.isEditing) {
          await this.edit(data);
        } else {
          await this.create(data);
        }

        setTimeout(async () => {
          this.loaded = false;
          await this.getUsers();
          this.loaded = true;
        }, 500);

        notify(
          {
            closeOnClick: true,
            message: this.editData.isEditing
              ? this.$t('editSuccess')
              : this.$t('contactCreatedSuccess'),
            width: 450,
          },
          'success',
          2000
        );
      } catch (error) {
        notify(
          {
            message: this.$t(`errors.${error.message}`),
            width: 450,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      }
      this.loaded = true;
    },
    validate() {
      if (!this.formData.name?.trim()) return false;
      if (!this.formData.surname?.trim()) return false;
      if (!this.formData.language) return false;
      if (!this.formData.phone?.trim()) return false;
      if (!this.formData.email?.trim()) return false;
      if (this.selectedAddresses.length < 1) return false;
      return true;
    },
    treeViewSelectionChanged(e) {
      this.syncSelection(e.component);
    },
    syncSelection(treeView) {
      const selectedAddresses = treeView
        .getSelectedNodes()
        .map((node) => node.itemData.addressCode);
      this.notEnoughAddressesSelected =
        selectedAddresses.length === 0;
      this.selectedAddresses = selectedAddresses;
    },
    syncSelectionEdit(treeView) {
      for (const addr of this.editData.data.addresses) {
        treeView.selectItem(addr.addressId);
        this.selectedAddresses.push(addr.code);
      }
    },
    treeViewContentReady(e) {
      e.component.unselectAll();

      if (
        this.editData.isEditing &&
        this.editData.data.addresses?.length > 0
      ) {
        this.syncSelectionEdit(e.component);
      }
    },
    translateLanguageOption(e) {
      if (e) return this.$t(e.value);
    },
    getLanguageObj(val) {
      return Object.values(ALL_LANGUAGES).find(
        (lang) => lang.key === val
      );
    },
    ...mapActions('contact', ['edit', 'create']),
    ...mapActions('company', ['getUsers']),
  },
};
</script>

<style>
.addresses-title {
  margin-bottom: 15px;
  font-weight: 600;
  color: #00346a;
}

.custom-treeview {
  border: none;
  border-radius: 0;
  margin: 5px 0 15px;
  width: 100% !important;
  height: 100% !important;
}

#botonGuardar {
  width: 10%;
  margin-top: 25px;
  background: var(--Primary-Blue-70, #00346a);
  border: 2px solid #00346a;
  color: #ffffff;
  border-radius: 10px;
  font-weight: 600;
  padding: 2px;
}

#botonGuardar:hover {
  background-color: #04509f;
  color: #fff;
}

.new-contact-form.page-content {
  /* Esto anula el padding-top de 55px impuesto por Framework7 y elimina el espacio blanco superior */

  padding: 0 10px !important;
  overflow-y: hidden !important;
  overflow-x: hidden !important;
}

/* El estilo que quieres para el contenedor de direcciones (si quieres que ocupe todo el espacio vertical) */
.treeview-container {
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 10px 20px;
  margin: 24px 0 10px;
  background-color: #f9f9f9;
  height: auto; /* Asegura que la altura sea flexible, no fija */
}

.justify-content-center .marginTop {
  margin-top: 0 !important;
}

.card-container {
  background-color: white; /* Fondo blanco */
  padding: 24px; /* Relleno dentro del card */
  border-radius: 10px; /* Esquinas redondeadas suaves */
  margin-top: 15px; /* Pequeño margen superior para separarlo del título */
}
</style>
